const FLAGS_PREFIX = '__fansly_group_flags_';
const LEGACY_SUBSCRIBERS = '__fansly_active_subscribers__';

/** System audiences shown as regular selectable Fansly list options. */
export const FANSLY_SYSTEM_AUDIENCE_OPTIONS = [
  { id: `${FLAGS_PREFIX}12__`, label: 'Include Subscribers' },
  { id: `${FLAGS_PREFIX}16__`, label: 'Include Expired Subscribers' },
  { id: `${FLAGS_PREFIX}2__`, label: 'Include Followers (Subscribers excluded)' },
  { id: `${FLAGS_PREFIX}32__`, label: 'Exclude Creators (Skips only unsubscribed)' },
  { id: `${FLAGS_PREFIX}64__`, label: 'Exclude Offline Users' },
] as const;

export function decodeFanslyAudience(values: string[] = []): { flags: number; listIds: string[] } {
  let flags = 32;
  let legacySubscribers = false;
  let explicitFlags = false;
  const listIds: string[] = [];
  for (const value of values) {
    if (value === LEGACY_SUBSCRIBERS) { legacySubscribers = true; continue; }
    if (value.startsWith(FLAGS_PREFIX)) {
      const match = /^__fansly_group_flags_(\d+)__$/.exec(value);
      const parsed = match ? Number(match[1]) : NaN;
      if (explicitFlags || !Number.isSafeInteger(parsed) || parsed < 0 || parsed > 126 || (parsed & ~126)) throw new Error('Invalid Fansly audience settings');
      explicitFlags = true;
      flags = parsed;
    } else listIds.push(value);
  }
  return { flags: flags | (legacySubscribers ? 12 : 0), listIds };
}

/** UI form decoder: several system options may be selected before saving. */
export function decodeFanslyAudiencePicker(values: string[] = []): { flags: number; listIds: string[] } {
  let flags = 0;
  const listIds: string[] = [];
  for (const value of values) {
    if (value === LEGACY_SUBSCRIBERS) { flags |= 12; continue; }
    if (value.startsWith(FLAGS_PREFIX)) {
      const match = /^__fansly_group_flags_(\d+)__$/.exec(value);
      const parsed = match ? Number(match[1]) : NaN;
      if (!Number.isSafeInteger(parsed) || parsed < 0 || parsed > 126 || (parsed & ~126)) throw new Error('Invalid Fansly audience settings');
      flags |= parsed;
    } else listIds.push(value);
  }
  return { flags, listIds };
}

export function fanslyAudiencePickerIds(values: string[]): string[] {
  const audience = decodeFanslyAudience(values);
  return [
    ...(audience.flags & 12 ? ['__fansly_group_flags_12__'] : []),
    ...(audience.flags & 16 ? ['__fansly_group_flags_16__'] : []),
    ...(audience.flags & 2 ? ['__fansly_group_flags_2__'] : []),
    ...(audience.flags & 32 ? ['__fansly_group_flags_32__'] : []),
    ...(audience.flags & 64 ? ['__fansly_group_flags_64__'] : []),
    ...audience.listIds,
  ];
}

export function encodeFanslyAudience(flags: number, listIds: string[]): string[] {
  if (!Number.isSafeInteger(flags) || flags < 0 || flags > 126 || (flags & ~126)) throw new Error('Invalid Fansly audience settings');
  return [`${FLAGS_PREFIX}${flags}__`, ...decodeFanslyAudience(listIds).listIds];
}

export function hasFanslyAudience(flags: number, listIds: string[]): boolean {
  return !!(flags & 30) || listIds.length > 0;
}

export function fanslyAudienceSummary(flags: number, listIds: string[]): string {
  const categories = [
    flags & 12 ? 'Subscribers' : '', flags & 16 ? 'Expired subscribers' : '',
    flags & 2 ? 'Followers' : '', listIds.length ? `${listIds.length} user lists` : '',
  ].filter(Boolean);
  return categories.join(' · ');
}
